import { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  InternalCreateTteValuationAdjustmentConfigMutationVariables,
  InternalUpdateTteValuationAdjustmentConfigMutationVariables,
  QuestionPriority,
  QuestionType,
  TteIndustryQuery,
  useInternalCreateTteValuationAdjustmentConfigMutation,
  useInternalDeleteTteValuationAdjustmentConfigMutation,
  useInternalUpdateTteIndustryDdqlTemplateMutation,
  useInternalUpdateTteValuationAdjustmentConfigMutation,
  useTteIndustriesQuery,
  useTteIndustryQuery,
} from "@/src/graphql/generated";
import useGqlClient from "@/src/hooks/useGqlClient";
import Loading from "@/src/components/Loading";
import { BackButton } from "@/src/components/BackButton";
import { Button } from "@/src/components/tailwind/Button";
import Dropdown, { Option } from "@/src/components/tailwind/Dropdown";
import { ChevronDownIcon, ChevronRightIcon } from "@heroicons/react/24/solid";

const MODEL_OPTIONS: Option[] = [
  { label: "claude-haiku-4-5 (default)", value: "anthropic/claude-haiku-4-5" },
  { label: "claude-sonnet-4-6", value: "anthropic/claude-sonnet-4-6" },
];
const DEFAULT_MODEL = "anthropic/claude-haiku-4-5";

// ---------------------------------------------------------------------------
// Industry list
// ---------------------------------------------------------------------------

export function PipelineIndustries() {
  const client = useGqlClient();
  const history = useHistory();
  const query = useTteIndustriesQuery(client);

  if (query.error) {
    return (
      <div className="p-4">
        <p className="text-gray-700 font-semibold">Something went wrong</p>
      </div>
    );
  }

  if (query.isLoading || !query.data) {
    return (
      <div className="p-4">
        <Loading />
      </div>
    );
  }

  return (
    <div className="divide-y divide-gray-100">
      {query.data.tteIndustries.map((ind) => (
        <button
          key={ind.id}
          className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 text-left"
          onClick={() => history.push(`/pipeline/industries/${ind.id}`)}
        >
          <div>
            <p className="text-sm font-semibold text-gray-900">{ind.industry}</p>
            <p className="text-xs text-gray-400">{ind.industryKey}</p>
          </div>
          <ChevronRightIcon className="w-4 h-4 text-gray-300" />
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Industry detail
// ---------------------------------------------------------------------------

export function PipelineIndustryDetail() {
  const { id } = useParams<{ id: string }>();
  const client = useGqlClient();
  const query = useTteIndustryQuery(client, { id });

  if (query.error) {
    return (
      <div className="p-4">
        <p className="text-gray-700 font-semibold">Something went wrong</p>
      </div>
    );
  }

  if (query.isLoading || !query.data) {
    return (
      <div className="p-4">
        <Loading />
      </div>
    );
  }

  const industry = query.data.tteIndustry;

  return (
    <div>
      <div className="flex items-center gap-x-3 p-4 border-b border-gray-100">
        <BackButton to="/pipeline/industries" size="sm" />
        <p className="font-semibold text-lg">{industry.industry}</p>
        <span className="text-xs text-gray-400 font-mono">{industry.industryKey}</span>
        <span className="ml-auto text-xs text-gray-500">
          Multiple range: {industry.multipleLow}x – {industry.multipleHigh}x
        </span>
      </div>

      <div className="p-4 space-y-8">
        <AdjustmentConfigsSection
          configs={industry.adjustmentConfigs}
          industryId={id}
        />
        <DdqlTemplateSection
          ddqlTemplate={industry.ddqlTemplate}
          industryId={id}
          adjustmentConfigs={industry.adjustmentConfigs}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Adjustment configs
// ---------------------------------------------------------------------------

type AdjustmentConfig =
  TteIndustryQuery["tteIndustry"]["adjustmentConfigs"][number];

function AdjustmentConfigsSection({
  configs,
  industryId,
}: {
  configs: AdjustmentConfig[];
  industryId: string;
}) {
  const [showCreate, setShowCreate] = useState(false);

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-semibold text-gray-900">Adjustment Configs</p>
        {!showCreate && (
          <button
            type="button"
            onClick={() => setShowCreate(true)}
            className="flex items-center gap-x-1.5 text-xs font-semibold text-persian-600 hover:text-persian-800"
          >
            <span className="text-base leading-none">+</span> Add config
          </button>
        )}
      </div>
      {configs.length === 0 && !showCreate ? (
        <p className="text-sm text-gray-400">No adjustment configs.</p>
      ) : (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wide">
              <tr>
                <th className="px-4 py-2 text-left w-8" />
                <th className="px-4 py-2 text-left">Name</th>
                <th className="px-4 py-2 text-left">Model</th>
                <th className="px-4 py-2 text-left">Premium</th>
                <th className="px-4 py-2 text-left">Neutral</th>
                <th className="px-4 py-2 text-left">Discount</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {configs.map((cfg) => (
                <AdjustmentConfigRow
                  key={cfg.id}
                  config={cfg}
                  industryId={industryId}
                />
              ))}
              {showCreate && (
                <CreateAdjustmentConfigRow
                  industryId={industryId}
                  onCancel={() => setShowCreate(false)}
                  onCreated={() => setShowCreate(false)}
                />
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Create adjustment config row
// ---------------------------------------------------------------------------

type CreateFields = {
  name: string;
  model: string;
  premiumDeltaLow: string;
  premiumDeltaHigh: string;
  neutralDeltaLow: string;
  neutralDeltaHigh: string;
  discountDeltaLow: string;
  discountDeltaHigh: string;
  premiumTrigger: string;
  discountTrigger: string;
  llmInstructions: string;
};

const BLANK_CREATE_FIELDS: CreateFields = {
  name: "",
  model: DEFAULT_MODEL,
  premiumDeltaLow: "0",
  premiumDeltaHigh: "0",
  neutralDeltaLow: "0",
  neutralDeltaHigh: "0",
  discountDeltaLow: "0",
  discountDeltaHigh: "0",
  premiumTrigger: "",
  discountTrigger: "",
  llmInstructions: "",
};

function CreateAdjustmentConfigRow({
  industryId,
  onCancel,
  onCreated,
}: {
  industryId: string;
  onCancel: () => void;
  onCreated: () => void;
}) {
  const client = useGqlClient();
  const queryClient = useQueryClient();
  const [fields, setFields] = useState<CreateFields>({ ...BLANK_CREATE_FIELDS });

  const mutation = useInternalCreateTteValuationAdjustmentConfigMutation(client, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["TteIndustry", { id: industryId }] });
      onCreated();
    },
  });

  function set(key: keyof CreateFields, value: string) {
    setFields((f) => ({ ...f, [key]: value }));
  }

  function handleCreate() {
    const vars: InternalCreateTteValuationAdjustmentConfigMutationVariables = {
      input: {
        name: fields.name,
        model: fields.model,
        industryId,
        premiumDeltaLow: parseFloat(fields.premiumDeltaLow),
        premiumDeltaHigh: parseFloat(fields.premiumDeltaHigh),
        neutralDeltaLow: parseFloat(fields.neutralDeltaLow),
        neutralDeltaHigh: parseFloat(fields.neutralDeltaHigh),
        discountDeltaLow: parseFloat(fields.discountDeltaLow),
        discountDeltaHigh: parseFloat(fields.discountDeltaHigh),
        premiumTrigger: fields.premiumTrigger,
        discountTrigger: fields.discountTrigger,
        llmInstructions: fields.llmInstructions || undefined,
      },
    };
    mutation.mutate(vars);
  }

  const canSubmit = fields.name.trim().length > 0;

  return (
    <>
      <tr className="bg-blue-50">
        <td className="px-4 py-2.5">
          <span className="text-xs text-blue-400 font-semibold">New</span>
        </td>
        <td className="px-4 py-2.5 font-medium text-gray-900" colSpan={5}>
          <input
            type="text"
            placeholder="Config name"
            value={fields.name}
            onChange={(e) => set("name", e.target.value)}
            className="w-full rounded border border-gray-300 px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-persian-500"
            autoFocus
          />
        </td>
      </tr>
      <tr className="bg-blue-50">
        <td colSpan={6} className="px-6 py-4">
          <div className="space-y-5">
            {/* Model */}
            <div className="w-64">
              <p className="text-xs font-semibold text-gray-600 mb-1.5">Model</p>
              <Dropdown
                options={MODEL_OPTIONS}
                selectedOption={
                  MODEL_OPTIONS.find((o) => o.value === fields.model) ??
                  MODEL_OPTIONS[0]
                }
                onSelect={(o) => set("model", o.value as string)}
              />
            </div>

            {/* Delta inputs */}
            <div className="grid grid-cols-3 gap-4">
              <DeltaInputGroup
                label="Premium"
                labelColor="text-green-700"
                low={fields.premiumDeltaLow}
                high={fields.premiumDeltaHigh}
                onLow={(v) => set("premiumDeltaLow", v)}
                onHigh={(v) => set("premiumDeltaHigh", v)}
              />
              <DeltaInputGroup
                label="Neutral"
                labelColor="text-gray-500"
                low={fields.neutralDeltaLow}
                high={fields.neutralDeltaHigh}
                onLow={(v) => set("neutralDeltaLow", v)}
                onHigh={(v) => set("neutralDeltaHigh", v)}
              />
              <DeltaInputGroup
                label="Discount"
                labelColor="text-amber-700"
                low={fields.discountDeltaLow}
                high={fields.discountDeltaHigh}
                onLow={(v) => set("discountDeltaLow", v)}
                onHigh={(v) => set("discountDeltaHigh", v)}
              />
            </div>

            {/* Triggers */}
            <div className="grid grid-cols-2 gap-4">
              <TextAreaField
                label="Premium trigger"
                labelColor="text-green-700"
                value={fields.premiumTrigger}
                onChange={(v) => set("premiumTrigger", v)}
                rows={2}
              />
              <TextAreaField
                label="Discount trigger"
                labelColor="text-amber-700"
                value={fields.discountTrigger}
                onChange={(v) => set("discountTrigger", v)}
                rows={2}
              />
            </div>

            {/* LLM instructions */}
            <TextAreaField
              label="LLM instructions"
              labelColor="text-gray-600"
              value={fields.llmInstructions}
              onChange={(v) => set("llmInstructions", v)}
              rows={5}
            />

            <div className="flex items-center justify-end gap-x-2">
              <Button
                text="Cancel"
                variant="neutral"
                size="s"
                onClick={onCancel}
                isDisabled={mutation.isPending}
              />
              <Button
                text="Create"
                variant="positive"
                size="s"
                isLoading={mutation.isPending}
                loadingText="Creating..."
                onClick={handleCreate}
                isDisabled={!canSubmit || mutation.isPending}
              />
            </div>
          </div>
        </td>
      </tr>
    </>
  );
}

// ---------------------------------------------------------------------------
// Edit / delete adjustment config row
// ---------------------------------------------------------------------------

type EditableFields = {
  model: string;
  premiumDeltaLow: string;
  premiumDeltaHigh: string;
  neutralDeltaLow: string;
  neutralDeltaHigh: string;
  discountDeltaLow: string;
  discountDeltaHigh: string;
  premiumTrigger: string;
  discountTrigger: string;
  llmInstructions: string;
};

function configToEditable(cfg: AdjustmentConfig): EditableFields {
  return {
    model: cfg.model || DEFAULT_MODEL,
    premiumDeltaLow: String(cfg.premiumDeltaLow),
    premiumDeltaHigh: String(cfg.premiumDeltaHigh),
    neutralDeltaLow: String(cfg.neutralDeltaLow),
    neutralDeltaHigh: String(cfg.neutralDeltaHigh),
    discountDeltaLow: String(cfg.discountDeltaLow),
    discountDeltaHigh: String(cfg.discountDeltaHigh),
    premiumTrigger: cfg.premiumTrigger,
    discountTrigger: cfg.discountTrigger,
    llmInstructions: cfg.llmInstructions,
  };
}

function AdjustmentConfigRow({
  config,
  industryId,
}: {
  config: AdjustmentConfig;
  industryId: string;
}) {
  const client = useGqlClient();
  const queryClient = useQueryClient();
  const [expanded, setExpanded] = useState(false);
  const [fields, setFields] = useState<EditableFields>(() =>
    configToEditable(config),
  );

  useEffect(() => {
    setFields(configToEditable(config));
  }, [config]);

  const updateMutation = useInternalUpdateTteValuationAdjustmentConfigMutation(
    client,
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["TteIndustry", { id: industryId }] });
      },
    },
  );

  const deleteMutation = useInternalDeleteTteValuationAdjustmentConfigMutation(
    client,
    {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["TteIndustry", { id: industryId }] });
      },
    },
  );

  function set(key: keyof EditableFields, value: string) {
    setFields((f) => ({ ...f, [key]: value }));
  }

  function isDirty() {
    const orig = configToEditable(config);
    return (Object.keys(fields) as (keyof EditableFields)[]).some(
      (k) => fields[k] !== orig[k],
    );
  }

  function handleSave() {
    const vars: InternalUpdateTteValuationAdjustmentConfigMutationVariables = {
      input: {
        id: config.id,
        model: fields.model,
        premiumDeltaLow: parseFloat(fields.premiumDeltaLow),
        premiumDeltaHigh: parseFloat(fields.premiumDeltaHigh),
        neutralDeltaLow: parseFloat(fields.neutralDeltaLow),
        neutralDeltaHigh: parseFloat(fields.neutralDeltaHigh),
        discountDeltaLow: parseFloat(fields.discountDeltaLow),
        discountDeltaHigh: parseFloat(fields.discountDeltaHigh),
        premiumTrigger: fields.premiumTrigger,
        discountTrigger: fields.discountTrigger,
        llmInstructions: fields.llmInstructions,
      },
    };
    updateMutation.mutate(vars);
  }

  function handleDelete() {
    deleteMutation.mutate({ id: config.id });
  }

  const dirty = isDirty();
  const isBusy = updateMutation.isPending || deleteMutation.isPending;

  return (
    <>
      <tr
        className="hover:bg-gray-50 cursor-pointer"
        onClick={() => setExpanded((v) => !v)}
      >
        <td className="px-4 py-2.5">
          {expanded ? (
            <ChevronDownIcon className="w-3.5 h-3.5 text-gray-400" />
          ) : (
            <ChevronRightIcon className="w-3.5 h-3.5 text-gray-400" />
          )}
        </td>
        <td className="px-4 py-2.5 font-medium text-gray-900">{config.name}</td>
        <td className="px-4 py-2.5 text-gray-500 font-mono text-xs">
          {config.model || DEFAULT_MODEL}
        </td>
        <td className="px-4 py-2.5 text-green-700 text-xs">
          {fmtDelta(config.premiumDeltaLow, config.premiumDeltaHigh)}
        </td>
        <td className="px-4 py-2.5 text-gray-500 text-xs">
          {fmtDelta(config.neutralDeltaLow, config.neutralDeltaHigh)}
        </td>
        <td className="px-4 py-2.5 text-amber-700 text-xs">
          {fmtDelta(config.discountDeltaLow, config.discountDeltaHigh)}
        </td>
      </tr>

      {expanded && (
        <tr className="bg-gray-50">
          <td colSpan={6} className="px-6 py-4">
            <div className="space-y-5">
              {/* Model */}
              <div className="w-64">
                <p className="text-xs font-semibold text-gray-600 mb-1.5">Model</p>
                <Dropdown
                  options={MODEL_OPTIONS}
                  selectedOption={
                    MODEL_OPTIONS.find((o) => o.value === fields.model) ??
                    MODEL_OPTIONS[0]
                  }
                  onSelect={(o) => set("model", o.value as string)}
                />
              </div>

              {/* Delta inputs */}
              <div className="grid grid-cols-3 gap-4">
                <DeltaInputGroup
                  label="Premium"
                  labelColor="text-green-700"
                  low={fields.premiumDeltaLow}
                  high={fields.premiumDeltaHigh}
                  onLow={(v) => set("premiumDeltaLow", v)}
                  onHigh={(v) => set("premiumDeltaHigh", v)}
                />
                <DeltaInputGroup
                  label="Neutral"
                  labelColor="text-gray-500"
                  low={fields.neutralDeltaLow}
                  high={fields.neutralDeltaHigh}
                  onLow={(v) => set("neutralDeltaLow", v)}
                  onHigh={(v) => set("neutralDeltaHigh", v)}
                />
                <DeltaInputGroup
                  label="Discount"
                  labelColor="text-amber-700"
                  low={fields.discountDeltaLow}
                  high={fields.discountDeltaHigh}
                  onLow={(v) => set("discountDeltaLow", v)}
                  onHigh={(v) => set("discountDeltaHigh", v)}
                />
              </div>

              {/* Triggers */}
              <div className="grid grid-cols-2 gap-4">
                <TextAreaField
                  label="Premium trigger"
                  labelColor="text-green-700"
                  value={fields.premiumTrigger}
                  onChange={(v) => set("premiumTrigger", v)}
                  rows={2}
                />
                <TextAreaField
                  label="Discount trigger"
                  labelColor="text-amber-700"
                  value={fields.discountTrigger}
                  onChange={(v) => set("discountTrigger", v)}
                  rows={2}
                />
              </div>

              {/* LLM instructions */}
              <TextAreaField
                label="LLM instructions"
                labelColor="text-gray-600"
                value={fields.llmInstructions}
                onChange={(v) => set("llmInstructions", v)}
                rows={5}
              />

              <div className="flex items-center justify-between">
                <Button
                  text="Delete"
                  variant="danger"
                  size="s"
                  isLoading={deleteMutation.isPending}
                  loadingText="Deleting..."
                  onClick={handleDelete}
                  isDisabled={isBusy}
                />
                <Button
                  text="Save"
                  variant="positive"
                  size="s"
                  isLoading={updateMutation.isPending}
                  loadingText="Saving..."
                  onClick={handleSave}
                  isDisabled={!dirty || isBusy}
                />
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function DeltaInputGroup({
  label,
  labelColor,
  low,
  high,
  onLow,
  onHigh,
}: {
  label: string;
  labelColor: string;
  low: string;
  high: string;
  onLow: (v: string) => void;
  onHigh: (v: string) => void;
}) {
  return (
    <div>
      <p className={`text-xs font-semibold mb-1.5 ${labelColor}`}>{label}</p>
      <div className="flex items-center gap-x-2">
        <DeltaInput label="Low" value={low} onChange={onLow} />
        <span className="text-gray-400 text-xs">–</span>
        <DeltaInput label="High" value={high} onChange={onHigh} />
      </div>
    </div>
  );
}

function DeltaInput({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex-1">
      <p className="text-xs text-gray-400 mb-0.5">{label}</p>
      <input
        type="number"
        step="0.1"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded border border-gray-300 px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-persian-500"
      />
    </div>
  );
}

function TextAreaField({
  label,
  labelColor,
  value,
  onChange,
  rows = 3,
}: {
  label: string;
  labelColor: string;
  value: string;
  onChange: (v: string) => void;
  rows?: number;
}) {
  return (
    <div>
      <p className={`text-xs font-semibold mb-1.5 ${labelColor}`}>{label}</p>
      <textarea
        rows={rows}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded border border-gray-300 px-3 py-2 text-xs focus:outline-none focus:ring-1 focus:ring-persian-500 resize-y"
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// DDQL template
// ---------------------------------------------------------------------------

type DdqlTemplate = TteIndustryQuery["tteIndustry"]["ddqlTemplate"];
type DdqlCategory = DdqlTemplate["categories"][number];
type DdqlQuestion = DdqlCategory["questions"][number];

type EditableQuestion = {
  title: string;
  body: string;
  priority: QuestionPriority;
  questionType: QuestionType;
  adjustmentConfigIds: string[];
};

type EditableCategory = {
  name: string;
  questions: EditableQuestion[];
};

function templateToEditable(ddqlTemplate: DdqlTemplate): EditableCategory[] {
  return ddqlTemplate.categories.map((cat) => ({
    name: cat.name,
    questions: cat.questions.map((q) => ({
      title: q.title,
      body: q.body,
      priority: q.priority,
      questionType: q.questionType,
      adjustmentConfigIds: [...q.adjustmentConfigIds],
    })),
  }));
}

function DdqlTemplateSection({
  ddqlTemplate,
  industryId,
  adjustmentConfigs,
}: {
  ddqlTemplate: DdqlTemplate;
  industryId: string;
  adjustmentConfigs: AdjustmentConfig[];
}) {
  const client = useGqlClient();
  const queryClient = useQueryClient();
  const [categories, setCategories] = useState<EditableCategory[]>(() =>
    templateToEditable(ddqlTemplate),
  );

  useEffect(() => {
    setCategories(templateToEditable(ddqlTemplate));
  }, [ddqlTemplate]);

  const mutation = useInternalUpdateTteIndustryDdqlTemplateMutation(client, {
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["TteIndustry", { id: industryId }] });
    },
  });

  function updateQuestion(
    catIdx: number,
    qIdx: number,
    patch: Partial<EditableQuestion>,
  ) {
    setCategories((prev) =>
      prev.map((cat, ci) =>
        ci !== catIdx
          ? cat
          : {
            ...cat,
            questions: cat.questions.map((q, qi) =>
              qi !== qIdx ? q : { ...q, ...patch },
            ),
          },
      ),
    );
  }

  function deleteQuestion(catIdx: number, qIdx: number) {
    setCategories((prev) =>
      prev.map((cat, ci) =>
        ci !== catIdx
          ? cat
          : { ...cat, questions: cat.questions.filter((_, qi) => qi !== qIdx) },
      ),
    );
  }

  function addQuestion(catIdx: number) {
    setCategories((prev) =>
      prev.map((cat, ci) =>
        ci !== catIdx
          ? cat
          : { ...cat, questions: [...cat.questions, { ...BLANK_QUESTION }] },

      ),
    );
  }

  function isDirty() {
    const orig = templateToEditable(ddqlTemplate);
    return JSON.stringify(categories) !== JSON.stringify(orig);
  }

  function handleSave() {
    mutation.mutate({
      input: {
        industryId,
        template: {
          categories: categories.map((cat) => ({
            name: cat.name,
            questions: cat.questions.map((q) => ({
              title: q.title,
              body: q.body,
              priority: q.priority,
              questionType: q.questionType,
              adjustmentConfigIds: q.adjustmentConfigIds,
              adjustmentFactors: [],
            })),
          })),
        },
      },
    });
  }

  const dirty = isDirty();

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <p className="text-sm font-semibold text-gray-900">DDQL Template</p>
        <Button
          text="Save template"
          variant="positive"
          size="s"
          isLoading={mutation.isPending}
          loadingText="Saving..."
          onClick={handleSave}
          isDisabled={!dirty}
        />
      </div>
      {categories.length === 0 ? (
        <p className="text-sm text-gray-400">No template configured.</p>
      ) : (
        <div className="space-y-4">
          {categories.map((cat, catIdx) => (
            <DdqlCategorySection
              key={catIdx}
              category={cat}
              catIdx={catIdx}
              adjustmentConfigs={adjustmentConfigs}
              onUpdateQuestion={(qIdx, patch) =>
                updateQuestion(catIdx, qIdx, patch)
              }
              onDeleteQuestion={(qIdx) => deleteQuestion(catIdx, qIdx)}
              onAddQuestion={() => addQuestion(catIdx)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

const BLANK_QUESTION: EditableQuestion = {
  title: "",
  body: "",
  priority: QuestionPriority.Medium,
  questionType: QuestionType.Question,
  adjustmentConfigIds: [],
};

function DdqlCategorySection({
  category,
  adjustmentConfigs,
  onUpdateQuestion,
  onDeleteQuestion,
  onAddQuestion,
}: {
  category: EditableCategory;
  catIdx: number;
  adjustmentConfigs: AdjustmentConfig[];
  onUpdateQuestion: (qIdx: number, patch: Partial<EditableQuestion>) => void;
  onDeleteQuestion: (qIdx: number) => void;
  onAddQuestion: () => void;
}) {
  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <div className="bg-gray-50 px-4 py-2 border-b border-gray-200">
        <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
          {category.name}
        </p>
        <p className="text-xs text-gray-400">{category.questions.length} questions</p>
      </div>
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-xs font-semibold text-gray-500 uppercase tracking-wide border-b border-gray-200">
          <tr>
            <th className="px-4 py-2 text-left w-8" />
            <th className="px-4 py-2 text-left">Title</th>
            <th className="px-4 py-2 text-left">Priority</th>
            <th className="px-4 py-2 text-left">Type</th>
            <th className="px-4 py-2 text-left">Adjustment factors</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {category.questions.map((q, qIdx) => (
            <DdqlQuestionRow
              key={qIdx}
              question={q}
              initiallyExpanded={q.title === ""}
              adjustmentConfigs={adjustmentConfigs}
              onChange={(patch) => onUpdateQuestion(qIdx, patch)}
              onDelete={() => onDeleteQuestion(qIdx)}
            />
          ))}
        </tbody>
      </table>
      <div className="px-4 py-2 border-t border-gray-100 bg-gray-50">
        <button
          type="button"
          onClick={onAddQuestion}
          className="flex items-center gap-x-1.5 text-xs font-semibold text-persian-600 hover:text-persian-800"
        >
          <span className="text-base leading-none">+</span> Add question
        </button>
      </div>
    </div>
  );
}

function DdqlQuestionRow({
  question,
  adjustmentConfigs,
  onChange,
  onDelete,
  initiallyExpanded = false,
}: {
  question: EditableQuestion;
  adjustmentConfigs: AdjustmentConfig[];
  onChange: (patch: Partial<EditableQuestion>) => void;
  onDelete: () => void;
  initiallyExpanded?: boolean;
}) {
  const [expanded, setExpanded] = useState(initiallyExpanded);

  return (
    <>
      <tr
        className="hover:bg-gray-50 cursor-pointer"
        onClick={() => setExpanded((v) => !v)}
      >
        <td className="px-4 py-2.5">
          {expanded ? (
            <ChevronDownIcon className="w-3.5 h-3.5 text-gray-400" />
          ) : (
            <ChevronRightIcon className="w-3.5 h-3.5 text-gray-400" />
          )}
        </td>
        <td className="px-4 py-2.5 font-medium text-gray-900">
          {question.title || <span className="text-gray-300 italic">Untitled</span>}
        </td>
        <td className="px-4 py-2.5">
          <PriorityBadge priority={question.priority} />
        </td>
        <td className="px-4 py-2.5 text-xs text-gray-500">
          {QUESTION_TYPE_LABELS[question.questionType]}
        </td>
        <td className="px-4 py-2.5">
          <div className="flex flex-wrap gap-1">
            {adjustmentConfigs
              .filter((cfg) => question.adjustmentConfigIds.includes(cfg.id))
              .map((cfg) => (
                <span
                  key={cfg.id}
                  className="inline-flex items-center rounded-full bg-persian-50 px-2 py-0.5 text-xs font-medium text-persian-700"
                >
                  {cfg.name}
                </span>
              ))}
          </div>
        </td>
      </tr>

      {expanded && (
        <tr className="bg-gray-50">
          <td colSpan={5} className="px-6 py-4">
            <div className="space-y-4">
              {/* Title */}
              <div>
                <p className="text-xs font-semibold text-gray-600 mb-1.5">Title</p>
                <input
                  type="text"
                  value={question.title}
                  onChange={(e) => onChange({ title: e.target.value })}
                  className="w-full rounded border border-gray-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-persian-500"
                />
              </div>

              {/* Body */}
              <div>
                <p className="text-xs font-semibold text-gray-600 mb-1.5">Body</p>
                <textarea
                  rows={3}
                  value={question.body}
                  onChange={(e) => onChange({ body: e.target.value })}
                  className="w-full rounded border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-persian-500 resize-y"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                {/* Priority */}
                <div>
                  <p className="text-xs font-semibold text-gray-600 mb-1.5">Priority</p>
                  <Dropdown
                    options={PRIORITY_OPTIONS}
                    selectedOption={
                      PRIORITY_OPTIONS.find((o) => o.value === question.priority) ??
                      PRIORITY_OPTIONS[0]
                    }
                    onSelect={(o) => onChange({ priority: o.value as QuestionPriority })}
                  />
                </div>

                {/* Question type */}
                <div>
                  <p className="text-xs font-semibold text-gray-600 mb-1.5">Type</p>
                  <Dropdown
                    options={QUESTION_TYPE_OPTIONS}
                    selectedOption={
                      QUESTION_TYPE_OPTIONS.find(
                        (o) => o.value === question.questionType,
                      ) ?? QUESTION_TYPE_OPTIONS[0]
                    }
                    onSelect={(o) => onChange({ questionType: o.value as QuestionType })}
                  />
                </div>
              </div>

              {/* Adjustment factors */}
              <div>
                <p className="text-xs font-semibold text-gray-600 mb-2">
                  Adjustment factors
                </p>
                {adjustmentConfigs.length === 0 ? (
                  <p className="text-xs text-gray-400">No adjustment configs defined for this industry.</p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {adjustmentConfigs.map((cfg) => {
                      const active = question.adjustmentConfigIds.includes(cfg.id);
                      return (
                        <button
                          key={cfg.id}
                          type="button"
                          onClick={() => {
                            const next = active
                              ? question.adjustmentConfigIds.filter((x) => x !== cfg.id)
                              : [...question.adjustmentConfigIds, cfg.id];
                            onChange({ adjustmentConfigIds: next });
                          }}
                          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium border transition-colors ${active
                              ? "bg-persian-600 text-white border-persian-600"
                              : "bg-white text-gray-500 border-gray-300 hover:border-persian-400"
                            }`}
                        >
                          {cfg.name}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="flex justify-end">
                <Button
                  text="Delete question"
                  variant="danger"
                  size="s"
                  onClick={onDelete}
                />
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function PriorityBadge({ priority }: { priority: QuestionPriority }) {
  const styles: Record<QuestionPriority, string> = {
    [QuestionPriority.Urgent]: "bg-red-100 text-red-600",
    [QuestionPriority.High]: "bg-amber-100 text-amber-600",
    [QuestionPriority.Medium]: "bg-yellow-100 text-yellow-600",
    [QuestionPriority.Low]: "bg-gray-100 text-gray-500",
    [QuestionPriority.None]: "bg-gray-50 text-gray-400",
  };
  return (
    <span
      className={`shrink-0 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${styles[priority]}`}
    >
      {priority}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Helpers & constants
// ---------------------------------------------------------------------------

function fmtDelta(low: number, high: number): string {
  const sign = (n: number) => (n >= 0 ? "+" : "");
  return `${sign(low)}${low.toFixed(1)}x – ${sign(high)}${high.toFixed(1)}x`;
}

const QUESTION_TYPE_LABELS: Record<QuestionType, string> = {
  [QuestionType.DocumentRequest]: "Document",
  [QuestionType.Question]: "Question",
};

const PRIORITY_OPTIONS = [
  { label: "Urgent", value: QuestionPriority.Urgent },
  { label: "High", value: QuestionPriority.High },
  { label: "Medium", value: QuestionPriority.Medium },
  { label: "Low", value: QuestionPriority.Low },
  { label: "None", value: QuestionPriority.None },
];

const QUESTION_TYPE_OPTIONS = [
  { label: "Question", value: QuestionType.Question },
  { label: "Document request", value: QuestionType.DocumentRequest },
];

